"use client";

import { useEffect, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionContext } from "@/contexts/session-context";
import { backendClient } from "@/lib/backend-api";

interface PylonChatSettings {
  app_id: string;
  email: string;
  name: string;
  email_hash?: string;
}

interface PylonApi {
  (command: string, ...args: unknown[]): void;
  q: unknown[][];
  e: (args: unknown[]) => void;
}

declare global {
  interface Window {
    pylon?: { chat_settings: PylonChatSettings };
    Pylon?: PylonApi;
  }
}

let pylonReady = false;
let pylonUnreadMessagesCount = 0;
let pylonEventsInstalled = false;
let outsideClickListenerTimeout: ReturnType<typeof setTimeout> | null = null;
const readyListeners = new Set<() => void>();
const unreadMessagesCountListeners = new Set<() => void>();

const notifyReadyListeners = () => {
  readyListeners.forEach((listener) => listener());
};

const notifyUnreadMessagesCountListeners = () => {
  unreadMessagesCountListeners.forEach((listener) => listener());
};

const markPylonReady = () => {
  if (pylonReady) {
    return;
  }
  pylonReady = true;
  notifyReadyListeners();
};

const resetPylonState = () => {
  const wasReady = pylonReady;
  const hadUnreadMessages = pylonUnreadMessagesCount > 0;

  pylonReady = false;
  pylonUnreadMessagesCount = 0;

  if (wasReady) {
    notifyReadyListeners();
  }
  if (hadUnreadMessages) {
    notifyUnreadMessagesCountListeners();
  }
};

export const usePylonReady = () =>
  useSyncExternalStore(
    (listener) => {
      readyListeners.add(listener);
      return () => readyListeners.delete(listener);
    },
    () => pylonReady,
    () => false,
  );

const updateUnreadMessagesCount = (count: unknown) => {
  const nextCount =
    typeof count === "number" && Number.isFinite(count)
      ? Math.max(0, count)
      : 0;

  if (pylonUnreadMessagesCount === nextCount) {
    return;
  }

  pylonUnreadMessagesCount = nextCount;
  notifyUnreadMessagesCountListeners();
};

export const usePylonUnreadMessagesCount = () =>
  useSyncExternalStore(
    (listener) => {
      unreadMessagesCountListeners.add(listener);
      return () => unreadMessagesCountListeners.delete(listener);
    },
    () => pylonUnreadMessagesCount,
    () => 0,
  );

export const openPylonChat = () => window.Pylon?.("show");

const closePylonOnOutsideClick = () => window.Pylon?.("hide");

const clearOutsideClickListener = () => {
  if (outsideClickListenerTimeout) {
    clearTimeout(outsideClickListenerTimeout);
    outsideClickListenerTimeout = null;
  }
  document.removeEventListener("click", closePylonOnOutsideClick);
};

const buildPylonChatSettings = (data: {
  appId: string;
  email: string;
  name: string;
  emailHash: string | null;
}): PylonChatSettings => ({
  app_id: data.appId,
  email: data.email,
  name: data.name,
  ...(data.emailHash ? { email_hash: data.emailHash } : {}),
});

const arePylonChatSettingsEqual = (
  current: PylonChatSettings | undefined,
  next: PylonChatSettings,
) =>
  current?.app_id === next.app_id &&
  current.email === next.email &&
  current.name === next.name &&
  current.email_hash === next.email_hash;

const installPylonClient = () => {
  if (!window.Pylon) {
    const pylonQueue: PylonApi = Object.assign(
      (command: string, ...args: unknown[]) => pylonQueue.e([command, ...args]),
      {
        q: [] as unknown[][],
        e: (args: unknown[]) => pylonQueue.q.push(args),
      },
    );
    window.Pylon = pylonQueue;
  }

  if (pylonEventsInstalled) {
    return;
  }

  // No always-visible launcher: the chat opens only via the Help button.
  window.Pylon("hideChatBubble");

  // onChangeUnreadMessagesCount fires once the widget has finished
  // initializing and synced for the user — the earliest point show() is
  // guaranteed to work, so we use it to enable the Help button.
  window.Pylon("onChangeUnreadMessagesCount", (count: unknown) => {
    markPylonReady();
    updateUnreadMessagesCount(count);
  });

  window.Pylon("onShow", () => {
    // Defer so the click that opened the chat doesn't immediately close it.
    clearOutsideClickListener();
    outsideClickListenerTimeout = setTimeout(() => {
      outsideClickListenerTimeout = null;
      document.addEventListener("click", closePylonOnOutsideClick);
    }, 0);
  });
  window.Pylon("onHide", () => {
    clearOutsideClickListener();
    window.Pylon?.("hideChatBubble");
  });

  pylonEventsInstalled = true;
};

export const resetPylonChat = () => {
  window.Pylon?.("hide");
  window.Pylon?.("hideChatBubble");
  window.Pylon?.("onChangeUnreadMessagesCount", null);
  window.Pylon?.("onShow", null);
  window.Pylon?.("onHide", null);

  clearOutsideClickListener();

  delete window.pylon;
  delete window.Pylon;
  pylonEventsInstalled = false;

  document
    .querySelectorAll<HTMLScriptElement>(
      'script[src^="https://widget.usepylon.com/widget/"]',
    )
    .forEach((script) => script.remove());

  resetPylonState();
};

export function PylonProvider() {
  const { authSessionData } = useSessionContext();
  const userId = authSessionData.user.id;

  const { data } = useQuery({
    queryKey: ["pylon", "chat-identity", userId],
    queryFn: () => backendClient.pylon.getChatIdentity(),
    enabled: Boolean(userId),
    staleTime: Infinity,
    retry: false,
  });

  // Install Pylon's queue shim immediately so event handlers register as soon
  // as the widget bundle loads, regardless of when the identity query resolves.
  useEffect(() => {
    installPylonClient();

    return () => {
      resetPylonChat();
    };
  }, []);

  // Boot the widget once we have the authenticated identity.
  useEffect(() => {
    if (!data) {
      return;
    }

    const chatSettings = buildPylonChatSettings(data);

    if (arePylonChatSettingsEqual(window.pylon?.chat_settings, chatSettings)) {
      return;
    }

    if (window.pylon) {
      resetPylonChat();
      installPylonClient();
    }

    window.pylon = {
      chat_settings: chatSettings,
    };

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://widget.usepylon.com/widget/${data.appId}`;
    document.head.appendChild(script);
  }, [data]);

  return null;
}
